import { useEffect } from "react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { toast } from "sonner";
import { Modal } from "react-bootstrap";
import { SubmitHandler, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createFileAttachement } from "../../../api/fileAttachement";

import withPermission from "../../../core/common/autorisations/withPermission";
import {
  Action,
  buildPermission,
  buildPermissionContext,
  Resource,
} from "../../../core/common/autorisations/permissionsMap";
import { createSanction } from "../../../api/etudiants";

//Typage des props parents
type Props = {
  show: boolean;
  metaData: any;
  onClose: () => void;
  onSaved?: () => void;
};

export enum TypeOFSanction {
  Normal = "Sanction Normale",
  DisciplinaryCouncil = "Conseil Disciplinaire",
}

export enum ReceivedSanction {
  Blame = "Blâme",
  TemporaryExclusion = "Exclusion Temporaire",
  DefinitiveExclusion = "Exclusion Définitive",
  Warning = "Avertissement",
}

const CreateSanctionModal = ({ show, metaData, onClose, onSaved }: Props) => {
  // Schéma de notre formulaire
  const schema = z.object({
    studentId: z.string().nonempty("L'identifiant de l'étudiant est requis"),
    sanctionType: z.nativeEnum(TypeOFSanction),
    reason: z.string().nonempty("La raison de la sanction est requise"),
    sanctionReceived: z.nativeEnum(ReceivedSanction),
    duration: z.string().nonempty("La durée de la sanction est requise"),
    files: z.array(z.instanceof(File)).optional(),
  });

  type FormFields = z.infer<typeof schema>;

  const {
    register,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  //Traitement après validation du formulaire de création d'une sanction
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      const formattedData = {
        type: data.sanctionType,
        reason_sanction: data.reason,
        sanction_received: data.sanctionReceived,
        duration: data.duration,
        id_studentProfil: data?.studentId || metaData?.Student?.id,
      };
      //Envoi des datas pour création vers le backend
      const newSanction = await createSanction(formattedData);

      if (files?.length > 0) {
        const formData = new FormData();
        formData.append("entity_type", "Sanction");
        formData.append("entity_id", newSanction.data.sanction?.id);
        files?.forEach((file: any) => {
          formData.append("files", file);
        });
        await createFileAttachement(
          formData,
          buildPermissionContext(Resource.STUDENT_SANCTION, Action.CREATE),
        );
      }
      reset(); //On réinitialise les champs du formulaire

      if (onSaved) onSaved(); // On fait un reload de la liste

      toast.success("Sanction ajoutée !"); //Toast de succès

      onClose(); // On ferme le modal
    } catch (error: any) {
      // Si erreur (Toast Error)
      // console.log("Erreur lors de l'ajout de la sanction: ",error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Une erreur s'est produit, Réessayer plus tard",
      );
    }
  };

  //Gestion des changements de fichier
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setValue("files", watch("files")?.concat(Array.from(files)));
    }
  };

  const files = watch("files") || [];

  useEffect(() => {
    setValue("studentId", metaData?.Student?.id || "");
    setValue("sanctionType", TypeOFSanction.Normal);
    setValue("reason", "");
    setValue("sanctionReceived", ReceivedSanction.Blame);
    setValue("duration", "");
    setValue("files", []);
  }, [metaData]);

  return (
    <>
      <Modal show={show} onHide={() => onClose()}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter une sanction</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form
            id="sanction-form"
            className="needs-validation"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="row row-cols-xxl-5 row-cols-md-6">
              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Étudiant <span className="text-danger">*</span>:{" "}
                  </label>{" "}
                  {metaData?.Student ? (
                    <span>
                      {metaData.Student.firstname} {metaData.Student.lastname}
                    </span>
                  ) : null}
                  {errors.studentId && (
                    <div className="invalid-feedback">
                      {errors.studentId.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Type de sanction <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("sanctionType")}
                    aria-label="Default select example"
                    className={
                      errors.sanctionType
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {Object.values(TypeOFSanction).map((type) => (
                      <option key={type} value={type}>
                        {type === TypeOFSanction.Normal ? "Sanction Disciplinaire" : type}
                      </option>
                    ))}
                  </select>
                  {errors.sanctionType && (
                    <div className="invalid-feedback">
                      {errors.sanctionType.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-6 col-xl-6 col-md-6">
                <div className="mb-3">
                  <label className="form-label">
                    Sanction reçu <span className="text-danger">*</span>
                  </label>
                  <select
                    {...register("sanctionReceived")}
                    aria-label="Default select example"
                    className={
                      errors.sanctionReceived
                        ? "form-select is-invalid"
                        : "form-select"
                    }
                  >
                    <option value={""}>Sélectionner</option>
                    {Object.values(ReceivedSanction).map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                  {errors.sanctionReceived && (
                    <div className="invalid-feedback">
                      {errors.sanctionReceived.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Raison de la sanction <span className="text-danger">*</span>
                    :{" "}
                  </label>
                  <br />
                  <textarea
                    {...register("reason")}
                    rows={3}
                    className={
                      errors.reason ? "form-control is-invalid" : "form-control"
                    }
                  />
                  {errors.reason && (
                    <div className="invalid-feedback">
                      {errors.reason.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">
                    Durée de la sanction <span className="text-danger">*</span>
                    :{" "}
                  </label>
                  <br />
                  <input
                    type="text"
                    {...register("duration")}
                    className={
                      errors.duration
                        ? "form-control is-invalid"
                        : "form-control"
                    }
                  />
                  {errors.duration && (
                    <div className="invalid-feedback">
                      {errors.duration.message}
                    </div>
                  )}
                </div>
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="mb-3">
                  <label className="form-label">Fichiers joints</label>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileChange}
                    className="form-control"
                  />
                </div>
                {files?.length > 0 && (
                  <div className="mb-3">
                    <label className="form-label">Fichiers sélectionnés</label>
                    <ul
                      className="list-group sc"
                      style={{ maxHeight: 200, overflowY: "auto" }}
                    >
                      {files.map((file, idx) => (
                        <li
                          key={idx}
                          className="list-group-item d-flex justify-content-between align-items-center"
                        >
                          <span>{file.name}</span>
                          <div>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary me-2"
                              onClick={() => {
                                const url = URL.createObjectURL(file);
                                window.open(url, "_blank");
                              }}
                            >
                              Voir
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => {
                                const newFiles = files.filter(
                                  (_, i) => i !== idx,
                                );
                                setValue("files", newFiles);
                              }}
                            >
                              Retirer
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="col-xxl-12 col-xl-12 col-md-12">
                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    className="btn btn-outline-danger me-2"
                    onClick={() => {
                      reset();
                      onClose();
                    }}
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    form="sanction-form"
                    className="btn btn-primary"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "En cours..." : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default withPermission(
  CreateSanctionModal,
  [buildPermission(Resource.STUDENT_SANCTION, Action.CREATE)],
  "masquer",
);
